import { useState, useRef, CSSProperties, useCallback, useEffect } from "react";

export function useDropdown<T extends HTMLElement>() {
  const [isOpen, setIsOpen] = useState(false);
  const [keepDropdown, setKeepDropdown] = useState(false);
  const containerRef = useRef<T | null>(null);
  const [styles, setStyles] = useState<CSSProperties>({});

  const recalculateStyles = useCallback(() => {
    const {
      bottom = 0,
      left = 0,
      width = 0,
    } = containerRef.current?.getBoundingClientRect() ?? {};
    setStyles({
      position: "fixed",
      top: `${bottom}px`,
      left: `${left}px`,
      width: `${width}px`,
    });
  }, [containerRef, setStyles]);

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev);
    recalculateStyles();
  }, [recalculateStyles]);

  const open = useCallback(() => {
    recalculateStyles();
    setIsOpen(true);
  }, [recalculateStyles, setIsOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const { target } = event;

      if (containerRef.current?.contains(target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleWindowScroll = () => {
      if (keepDropdown) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyup = (event: KeyboardEvent) => {
      const { code } = event;
      if (code !== "Escape") {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("keyup", handleKeyup);
    if (!keepDropdown) {
      window.addEventListener("scroll", handleWindowScroll);
    }
    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("keyup", handleKeyup);
      window.removeEventListener("scroll", handleWindowScroll);
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [keepDropdown]);

  return {
    containerRef,
    isOpen,
    styles,
    setKeepDropdown,
    open,
    toggle,
    close,
  };
}
